import { gsap } from "gsap";
import { Application, Assets, Sprite } from "pixi.js";
import type { Texture } from "pixi.js";

const PARTICLE_DURATION_MS = 700;
const PARTICLE_CANVAS_CLASS = "prize-particle-canvas";

interface PrizeParticleOptions {
    cluster: string;
    particleCount: number;
    particleImages: readonly string[];
    startElementSelector: string;
    scale?: number;
    delayMs?: number;
    onAlmostFinished?: () => void;
    onFinished?: () => void;
}

interface ParticleCluster {
    remaining: number;
    sprites: Set<Sprite>;
    timelines: Set<gsap.core.Timeline>;
    almostFinishedTimer: number;
    onFinished?: () => void;
}

function randomBetween(min: number, max: number): number {
    return min + Math.random() * (max - min);
}

function randomNonZeroBetween(min: number, max: number): number {
    let value = 0;
    while (value === 0) value = randomBetween(min, max);
    return value;
}

/**
 * Pixi surface + GSAP "towards camera" payout path used by alpha-mini-app.
 *
 * Slot particles do not use alpha-mini-app' persistent physics ticker. They start
 * around #particle-source at scale zero, arc upward, then accelerate down
 * while growing to 4x their visible size over roughly 700ms.
 */
class PrizeParticleService {
    private app: Application | null = null;
    private appPromise: Promise<Application> | null = null;
    private readonly clusters = new Map<string, ParticleCluster>();
    private readonly spritePool: Sprite[] = [];
    private readonly generationIds = new Map<string, number>();

    async preload(images: readonly string[]): Promise<void> {
        await this.loadTextures(images);
    }

    async generate(options: PrizeParticleOptions): Promise<boolean> {
        if (options.particleCount <= 0 || options.particleImages.length === 0) {
            return false;
        }

        const generationId = (this.generationIds.get(options.cluster) ?? 0) + 1;
        this.generationIds.set(options.cluster, generationId);
        this.removeCluster(options.cluster);

        const source = document.querySelector<HTMLElement>(
            options.startElementSelector,
        );
        if (!source) {
            console.error(
                `[prize-particles] missing origin ${options.startElementSelector}`,
            );
            return false;
        }

        const [app, textures] = await Promise.all([
            this.ensureApp(),
            this.loadTextures(options.particleImages),
        ]);
        if (this.generationIds.get(options.cluster) !== generationId) {
            return false;
        }

        const count = Math.max(0, Math.round(options.particleCount));
        if (count === 0) return false;

        app.renderer.resize(window.innerWidth, window.innerHeight);
        const sourceRect = source.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const isMobile = /Android|iPad|iPhone|iPod/i.test(navigator.userAgent);
        const halfHorizontalSpread =
            viewportWidth * (isMobile ? 1 : 0.7) * 0.75;
        const sourceCenterX = sourceRect.left + sourceRect.width / 2;
        const sourceCenterY = sourceRect.top + sourceRect.height / 2;
        const sourceHalfWidth = sourceRect.width / 2;
        const sourceHalfHeight = sourceRect.height / 2;
        const baseScale = options.scale ?? 0.3;
        const delayMs = options.delayMs ?? 1;
        const upDurationSeconds = (PARTICLE_DURATION_MS * 0.3) / 1000;
        const downDurationSeconds = (PARTICLE_DURATION_MS * 0.6) / 1000;
        const fullDurationSeconds = upDurationSeconds + downDurationSeconds;

        const cluster: ParticleCluster = {
            remaining: count,
            sprites: new Set(),
            timelines: new Set(),
            almostFinishedTimer: window.setTimeout(
                () => options.onAlmostFinished?.(),
                PARTICLE_DURATION_MS * 0.85,
            ),
            onFinished: options.onFinished,
        };
        this.clusters.set(options.cluster, cluster);

        for (let index = 0; index < count; index += 1) {
            const texture = textures[index % textures.length]!;
            const sprite = this.takeSprite(texture);
            const naturalSize = Math.max(texture.width, texture.height, 1);
            // alpha-mini-app' atlas frames are roughly 100 logical pixels. Normalize
            // our larger standalone WebPs to that basis before applying its
            // exact 0.3–0.55 scale range.
            const assetScale = 100 / naturalSize;
            const visibleScale =
                (baseScale + randomBetween(0, 0.25)) * assetScale;
            const cameraScale = visibleScale * 4;

            const startX =
                sourceCenterX +
                randomBetween(-sourceHalfWidth, sourceHalfWidth);
            const startY =
                sourceCenterY +
                randomBetween(-sourceHalfHeight, sourceHalfHeight);
            const finalX =
                startX +
                randomNonZeroBetween(
                    -halfHorizontalSpread,
                    halfHorizontalSpread,
                );
            const peakY =
                startY -
                randomBetween(viewportHeight * 0.1, viewportHeight * 0.75);
            const finalY = startY + viewportHeight;
            const startingRotation = randomBetween(0, Math.PI * 2);
            const finalRotation =
                startingRotation + randomBetween(Math.PI, Math.PI * 2);

            sprite.position.set(startX, startY);
            sprite.rotation = startingRotation;
            sprite.scale.set(0);
            sprite.alpha = 1;
            sprite.visible = true;
            app.stage.addChild(sprite);
            cluster.sprites.add(sprite);

            // let timeline!: gsap.core.Timeline;
            // timeline = gsap.timeline({
            //     delay: (index * delayMs) / 1000,
            //     onComplete: () =>
            //         this.finishParticle(options.cluster, sprite, timeline),
            // });
            const timeline = gsap.timeline({
                delay: (index * delayMs) / 1000,
                onComplete: () =>
                    this.finishParticle(options.cluster, sprite, timeline),
            });
            cluster.timelines.add(timeline);

            timeline
                .to(
                    sprite.position,
                    {
                        x: finalX,
                        duration: fullDurationSeconds,
                        ease: "power2.out",
                    },
                    0,
                )
                .to(
                    sprite.position,
                    {
                        y: peakY,
                        duration: upDurationSeconds,
                        ease: "circ.out",
                    },
                    0,
                )
                .to(
                    sprite.position,
                    {
                        y: finalY,
                        duration: downDurationSeconds,
                        ease: "power2.in",
                    },
                    upDurationSeconds,
                )
                .to(
                    sprite.scale,
                    {
                        x: visibleScale,
                        y: visibleScale,
                        duration: upDurationSeconds,
                        ease: "circ.out",
                    },
                    0,
                )
                .to(
                    sprite.scale,
                    {
                        x: cameraScale,
                        y: cameraScale,
                        duration: downDurationSeconds,
                        ease: "power2.in",
                    },
                    upDurationSeconds,
                )
                .to(
                    sprite,
                    {
                        rotation: finalRotation,
                        duration: fullDurationSeconds,
                        ease: "none",
                    },
                    0,
                );
        }

        const canvas = app.canvas as HTMLCanvasElement;
        canvas.style.display = "block";
        app.ticker.start();
        return true;
    }

    clear(cluster: string): void {
        this.generationIds.set(
            cluster,
            (this.generationIds.get(cluster) ?? 0) + 1,
        );
        this.removeCluster(cluster);
        if (this.clusters.size === 0) this.stopRenderer();
    }

    private async ensureApp(): Promise<Application> {
        if (this.app) return this.app;
        if (this.appPromise) return this.appPromise;

        this.appPromise = (async () => {
            const app = new Application();
            await app.init({
                resizeTo: window,
                backgroundAlpha: 0,
                // A fullscreen DPR=3 canvas is expensive in Telegram's iOS
                // WebView and does not improve these short-lived sprites.
                resolution: Math.min(window.devicePixelRatio || 1, 2),
                autoDensity: true,
                antialias: false,
                preference: "webgl",
                powerPreference: "high-performance",
                preserveDrawingBuffer: false,
            });

            const canvas = app.canvas as HTMLCanvasElement;
            canvas.className = PARTICLE_CANVAS_CLASS;
            canvas.setAttribute("aria-hidden", "true");
            Object.assign(canvas.style, {
                position: "absolute",
                top: "0",
                left: "0",
                zIndex: "1001",
                display: "none",
                width: "100%",
                height: "100%",
                pointerEvents: "none",
            });
            document.body.appendChild(canvas);

            app.stage.eventMode = "none";
            if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
                app.ticker.maxFPS = 45;
                app.ticker.speed = 0.75;
            } else {
                app.ticker.maxFPS = 60;
            }
            app.ticker.stop();

            document.addEventListener("visibilitychange", () => {
                if (document.hidden) {
                    app.ticker.stop();
                } else if (this.clusters.size > 0) {
                    app.ticker.start();
                }
            });

            this.app = app;
            return app;
        })();

        try {
            return await this.appPromise;
        } catch (error) {
            this.appPromise = null;
            throw error;
        }
    }

    private async loadTextures(images: readonly string[]): Promise<Texture[]> {
        return Promise.all(
            [...new Set(images)].map((image) => Assets.load<Texture>(image)),
        );
    }

    private takeSprite(texture: Texture): Sprite {
        const sprite = this.spritePool.pop() ?? new Sprite(texture);
        sprite.texture = texture;
        sprite.anchor.set(0.5);
        return sprite;
    }

    private recycleSprite(sprite: Sprite): void {
        sprite.removeFromParent();
        sprite.visible = false;
        sprite.alpha = 0;
        this.spritePool.push(sprite);
    }

    private finishParticle(
        clusterName: string,
        sprite: Sprite,
        timeline: gsap.core.Timeline,
    ): void {
        const cluster = this.clusters.get(clusterName);
        if (!cluster || !cluster.sprites.delete(sprite)) return;

        cluster.timelines.delete(timeline);
        this.recycleSprite(sprite);
        cluster.remaining -= 1;
        if (cluster.remaining > 0) return;

        window.clearTimeout(cluster.almostFinishedTimer);
        cluster.onFinished?.();
        this.clusters.delete(clusterName);
        if (this.clusters.size === 0) this.stopRenderer();
    }

    private removeCluster(clusterName: string): void {
        const cluster = this.clusters.get(clusterName);
        if (!cluster) return;

        window.clearTimeout(cluster.almostFinishedTimer);
        cluster.timelines.forEach((timeline) => timeline.kill());
        cluster.sprites.forEach((sprite) => this.recycleSprite(sprite));
        this.clusters.delete(clusterName);
    }

    private stopRenderer(): void {
        if (!this.app) return;
        this.app.render();
        this.app.ticker.stop();
        (this.app.canvas as HTMLCanvasElement).style.display = "none";
    }
}

const prizeParticleService = new PrizeParticleService();

export function preloadPrizeParticleImages(
    images: readonly string[],
): Promise<void> {
    return prizeParticleService.preload(images);
}

export function generatePrizeParticles(
    options: PrizeParticleOptions,
): Promise<boolean> {
    return prizeParticleService.generate(options);
}

export function clearPrizeParticleCluster(cluster: string): void {
    prizeParticleService.clear(cluster);
}
